import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { isCoarsePointer, prefersReducedMotion } from "./usePwaEnv";

/**
 * `PullToRefresh` — an app-like, RTL-agnostic pull-to-refresh wrapper. [task #15]
 *
 * On a touch / coarse-pointer device (or a narrow viewport), dragging down from
 * the very top of the document reveals a branded spinner with progressive
 * resistance; releasing past a threshold fires `onRefresh`. The gesture is
 * vertical only, so it behaves identically in LTR and RTL. It is inert on
 * desktop (so it never steals mouse-wheel scroll) and under
 * `prefers-reduced-motion` (no pull travel; a simple tap-to-refresh affordance
 * is offered instead once the user reaches the top).
 *
 * Storybook/SSR safe: no browser globals are touched at module scope; all
 * feature detection happens inside effects/handlers.
 */

interface PullToRefreshProps {
  children: React.ReactNode;
  /**
   * Refresh handler. May be async; the spinner stays until it resolves. When
   * omitted the component falls back to `window.location.reload()`.
   */
  onRefresh?: () => void | Promise<void>;
  className?: string;
}

/** Drag distance (px) past which a release triggers a refresh. */
const THRESHOLD = 72;
/** Max travel the indicator is allowed to move (rubber-band ceiling). */
const MAX_PULL = 96;

export function PullToRefresh({ children, onRefresh, className }: PullToRefreshProps) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const startY = useRef<number | null>(null);
  const active = useRef(false);

  // Settle environment probes after mount so the static (Storybook) render is a
  // plain pass-through wrapper that never reads touch/matchMedia state.
  useEffect(() => {
    setEnabled(isCoarsePointer());
    setReduced(prefersReducedMotion());
  }, []);

  const runRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (onRefresh) {
        await onRefresh();
      } else if (typeof window !== "undefined") {
        window.location.reload();
        return; // page is about to unload
      }
    } finally {
      setRefreshing(false);
      setPull(0);
    }
  }, [onRefresh]);

  // Touch gesture — only wired when enabled (coarse pointer) and motion is on.
  useEffect(() => {
    if (!enabled || reduced) return;

    const atTop = () =>
      (window.scrollY || document.documentElement.scrollTop || 0) <= 0;

    const onStart = (e: TouchEvent) => {
      if (refreshing || !atTop() || e.touches.length !== 1) return;
      startY.current = e.touches[0].clientY;
      active.current = true;
    };

    const onMove = (e: TouchEvent) => {
      if (!active.current || startY.current === null) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta <= 0) {
        // Scrolling up / no downward intent — abandon the gesture.
        active.current = false;
        startY.current = null;
        setPull(0);
        return;
      }
      // Progressive resistance: the further you pull the slower it moves.
      const resisted = Math.min(MAX_PULL, delta * 0.5);
      setPull(resisted);
      // Prevent the native overscroll/bounce only while we own the gesture.
      if (e.cancelable) e.preventDefault();
    };

    const onEnd = () => {
      if (!active.current) return;
      active.current = false;
      startY.current = null;
      setPull((current) => {
        if (current >= THRESHOLD * 0.5) void runRefresh();
        return current >= THRESHOLD * 0.5 ? current : 0;
      });
    };

    // `passive: false` is required so `preventDefault` can suppress native bounce.
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd);
    window.addEventListener("touchcancel", onEnd);
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, [enabled, reduced, refreshing, runRefresh]);

  const armed = pull >= THRESHOLD * 0.5;
  const progress = Math.min(1, pull / THRESHOLD);
  const showIndicator = refreshing || pull > 0;

  return (
    <div className={cn("relative", className)}>
      {/* Pull indicator — only for the animated (motion-on) path. */}
      {enabled && !reduced && showIndicator && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 z-40 flex justify-center"
          style={{ transform: `translateY(${Math.max(0, (refreshing ? THRESHOLD : pull) - 28)}px)` }}
        >
          <div className="grid h-9 w-9 place-items-center rounded-full border border-line bg-card text-primary shadow-lift">
            <span
              className={cn("block", refreshing && "motion-safe:animate-spin")}
              style={refreshing ? undefined : { transform: `rotate(${progress * 270}deg)` }}
            >
              <Spinner />
            </span>
          </div>
        </div>
      )}

      {/* Live region so screen-reader users hear the state change. */}
      <span className="sr-only" role="status" aria-live="polite">
        {refreshing ? t("pwa.refreshing") : armed ? t("pwa.releaseToRefresh") : ""}
      </span>

      {/* Content translates with the pull on the motion path; static otherwise. */}
      <div
        className={cn(!active.current && "motion-safe:transition-transform motion-safe:duration-200")}
        style={enabled && !reduced && pull > 0 ? { transform: `translateY(${pull}px)` } : undefined}
      >
        {children}
      </div>

      {/* Reduced-motion fallback: a plain, always-available refresh button. */}
      {enabled && reduced && (
        <button
          type="button"
          onClick={() => void runRefresh()}
          disabled={refreshing}
          className="fixed bottom-20 end-4 z-40 flex items-center gap-2 rounded-full border border-line bg-card px-3.5 py-2 text-[12.5px] font-bold text-ink-1 shadow-lift disabled:opacity-60"
        >
          <Spinner />
          {refreshing ? t("pwa.refreshing") : t("pwa.pullToRefresh")}
        </button>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 4v5h-5" />
    </svg>
  );
}
